import { PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppInputSearch from '@nxsys/components/AppInputSearch';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import {
  useCreatePaymentEmployee,
  useUpdatePaymentEmployee,
} from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { GetPaymentEmployeeListParams } from '@nxsys/store/slices/employee/types';
import { Payment } from '@nxsys/store/slices/paymentSetting/types';
import {
  CreatePaymentEmployeeParams,
  UpdatePaymentEmployeeParams,
} from '@nxsys/types/api';
import { UMBRELLA_PAYE_HYBRID } from '@nxsys/constants/AppConst';
import { isCISPaymentModel } from '@nxsys/helpers/Common';
import { Icons } from '@nxsys/types/icons';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { showToast } from '@nxsys/utils/toast';
import { ColumnsType } from 'antd/lib/table';
import { debounce, toNumber } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CreatePaymentModal from './CreatePaymentModal';
import EditPaymentModal from './EditPaymentModal';
import { PaymentFormValues } from './PaymentForm';
import { Tag, Tooltip } from 'antd';
import { defaultRepetitionOptions } from '@nxsys/constants/AppConstants';


type Props = {
  employeeId: string;
  paymentModel?: string;
};

const PaymentComponent: React.FC<Props> = ({ employeeId, paymentModel }) => {
  const { messages } = useTypedIntl();
  const dispatch = useDispatch<AppDispatch>();
  const { isBureau, payrollCountry, isEmployee } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const isCISWorker = isCISPaymentModel(paymentModel);
  const isUmbrellaPAYE = UMBRELLA_PAYE_HYBRID.includes(paymentModel ?? '');

  const { paymentList } = useSelector((state: AppState) => state.employeeStore);
  const { list, pagination, loading, queryParams } = paymentList;

  const handleGetPayments = (params: GetPaymentEmployeeListParams) => {
    const newParams = {
      ...params,
      id: employeeId,
    };
    dispatch(employeeActions.getPaymentEmployeeList(newParams));
  };

  const refetchData = () => {
    handleGetPayments({ ...queryParams });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    handleGetPayments({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleGetPayments({
        ...queryParams,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleGetPayments({
        ...queryParams,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const debouncedFetchData = useCallback(
    debounce((search: string) => {
      handleGetPayments({
        ...queryParams,
        page: 0,
        size: 20,
        search: search,
      });
    }, 500),
    [queryParams],
  );

  const onChangeSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    debouncedFetchData(value);
  };

  useEffect(() => {
    handleGetPayments({ page: 0, size: 20 });
  }, []);

  const columns: ColumnsType<Payment> = [
    {
      key: 'action',
      title: messages['common.action'],
      fixed: 'left',
      align: 'left',
      render: (record) => (
        <ul className="table-actions flex items-center justify-start">
          <li onClick={() => toggleEditPayment(record)}>
            <Tooltip title="Edit">
              <span>
                <Icons.IconEdit />
              </span>
            </Tooltip>
          </li>
        </ul>
      ),
      width: 100,
    },
    {
      key: 'paymentCode',
      title: messages['paymentSetting.payment.paymentCode'],
      dataIndex: 'paymentCode',
      fixed: 'left',
    },
    {
      key: 'description',
      title: messages['common.description'],
      dataIndex: 'description',
    },
    ...((isIreland
      ? [
          {
            key: 'repetition',
            title: messages['common.repetition'],
            dataIndex: 'repetition',
            width: 300,
            render: (value) =>
              defaultRepetitionOptions.find((option) => option.value === value)
                ?.label || '-',
          },
        ]
      : []) as ColumnsType<Payment>),
    ...(!isBureau
      ? [
          {
            key: 'paymentType',
            title: messages['common.paymentType'],
            dataIndex: 'paymentType',
          },
        ]
      : ([] as ColumnsType<Payment>)),
    {
      key: 'minPaymentPeriod',
      title: messages['paymentSetting.payment.minPaymentPeriod'],
      dataIndex: 'minPaymentPeriod',
      render: (value) =>
        value ? formatMoney(value, showCurrencyByCountry(payrollCountry)) : '-',
    },
    {
      key: 'startDate',
      title: messages['common.startDate'],
      dataIndex: 'startDate',
      render: (value) =>
        value ? moment(value, 'MM-DD-YYYY').format('DD/MM/YYYY') : '-',
    },
    {
      key: 'endDate',
      title: messages['common.endDate'],
      dataIndex: 'endDate',
      render: (value) =>
        value ? moment(value, 'MM-DD-YYYY').format('DD/MM/YYYY') : '-',
    },
    {
      key: 'maxRecurrence',
      title: messages['paymentSetting.payment.maxRecurrence'],
      dataIndex: 'maxRecurrence',
    },
    {
      key: 'totalPayment',
      title: messages['common.totalPayment'],
      dataIndex: 'totalPayment',
      render: (value) =>
        value ? formatMoney(value, showCurrencyByCountry(payrollCountry)) : '-',
    },
    ...((!isIreland && isCISWorker
      ? [
          {
            key: 'includeInCisCalculation',
            title: messages['common.beforeCISTax'],
            dataIndex: 'includeInCisCalculation',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
        ]
      : []) as ColumnsType<Payment>),
    ...((!isIreland && isUmbrellaPAYE
      ? [
          {
            key: 'beforeNI',
            title: messages['common.beforeNI'],
            dataIndex: 'beforeNI',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
          {
            key: 'beforeTax',
            title: messages['common.beforeTax'],
            dataIndex: 'beforeTax',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
          {
            key: 'beforePension',
            title: messages['common.beforePension'],
            dataIndex: 'beforePension',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
        ]
      : []) as ColumnsType<Payment>),
    ...((isIreland
      ? [
          {
            key: 'beforeTax',
            title: messages['common.beforeTax'],
            dataIndex: 'beforeTax',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
          {
            key: 'employeePRSICanBeDeducted',
            title: messages['common.beforeEmployeePRSI'],
            dataIndex: 'employeePRSICanBeDeducted',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
          {
            key: 'employerPRSICanBeDeducted',
            title: messages['common.beforeEmployerPRSI'],
            dataIndex: 'employerPRSICanBeDeducted',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
          {
            key: 'uscCanBeDeducted',
            title: messages['common.beforeUSC'],
            dataIndex: 'uscCanBeDeducted',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
          {
            key: 'ascCanBeDeducted',
            title: messages['common.beforeASC'],
            dataIndex: 'ascCanBeDeducted',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
          {
            key: 'pensionSchemaDeductionCanBeDeducted',
            title: messages['common.beforePensionSchemeDeductions'],
            dataIndex: 'pensionSchemaDeductionCanBeDeducted',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
            width: 300,
          },
          {
            key: 'includeInCisCalculation',
            title: messages['common.beforeRCT'],
            dataIndex: 'includeInCisCalculation',
            render: (value) => (
              <Tag>
                {value ? messages['common.yes'] : messages['common.no']}
              </Tag>
            ),
          },
        ]
      : []) as ColumnsType<Payment>),
  ];

  const [openCreatePayment, setOpenCreatePayment] = useState(false);
  const toggleCreatePayment = () => {
    setOpenCreatePayment((prev) => !prev);
  };
  const { run: createPaymentEmployee } = useCreatePaymentEmployee();
  const onCreatedPayment = async (values: PaymentFormValues) => {
    let params: CreatePaymentEmployeeParams;
    if (isIreland) {
      params = {
        paymentCode: null,
        paymentType: values?.paymentType?.value || null,
        description: values.description ?? '',
        startDate: values.startDate?.format('MM-DD-YYYY') ?? null,
        endDate: values.endDate?.format('MM-DD-YYYY') ?? null,
        minPaymentPeriod: values.minPaymentPeriod
          ? toNumber(values.minPaymentPeriod)
          : null,
        maxRecurrence: values.maxRecurrence
          ? toNumber(values.maxRecurrence)
          : null,
        totalPayment: values.totalPayment || null,
        includeInCisCalculation: values.includeInCisCalculation,
        repetition: values.repetition?.value || null,
        beforeTax: values.beforeTax,
        // NX-5656: the Ireland calc reads taxCanBeDeducted, not beforeTax —
        // keep both in sync so the toggle actually reaches the calculation
        taxCanBeDeducted: values.beforeTax,
        employeePRSICanBeDeducted: values.employeePRSICanBeDeducted,
        employerPRSICanBeDeducted: values.employerPRSICanBeDeducted,
        uscCanBeDeducted: values.uscCanBeDeducted,
        ascCanBeDeducted: values.ascCanBeDeducted,
        pensionSchemaDeductionCanBeDeducted:
          values.pensionSchemaDeductionCanBeDeducted,
        lptCanBeDeducted: values.lptCanBeDeducted,
      };
    } else if (isCISWorker) {
      params = {
        paymentCode: null,
        paymentType: values?.paymentType?.value || null,
        description: values.description ?? '',
        startDate: values.startDate?.format('MM-DD-YYYY') ?? null,
        endDate: values.endDate?.format('MM-DD-YYYY') ?? null,
        minPaymentPeriod: values.minPaymentPeriod
          ? toNumber(values.minPaymentPeriod)
          : null,
        maxRecurrence: values.maxRecurrence
          ? toNumber(values.maxRecurrence)
          : null,
        totalPayment: values.totalPayment || null,
        includeInCisCalculation: values.includeInCisCalculation,
      };
    } else {
      params = {
        paymentCode: null,
        paymentType: values?.paymentType?.value || null,
        description: values.description ?? '',
        startDate: values.startDate?.format('MM-DD-YYYY') ?? null,
        endDate: values.endDate?.format('MM-DD-YYYY') ?? null,
        minPaymentPeriod: values.minPaymentPeriod
          ? toNumber(values.minPaymentPeriod)
          : null,
        maxRecurrence: values.maxRecurrence
          ? toNumber(values.maxRecurrence)
          : null,
        totalPayment: values.totalPayment || null,
        beforeTax: values.beforeTax,
        beforeNI: values.beforeNI,
        beforePension: values.beforePension,
      };
    }
    const res = await createPaymentEmployee(employeeId, params);
    if (res.success) {
      showToast({
        type: 'success',
        message: messages['common.addSuccess'],
      });
      refetchData();
    }
    toggleCreatePayment();
  };

  const [openEditPayment, setOpenEditPayment] = useState(false);
  const [paymentSelected, setPaymentSelected] = useState<Payment | null>(null);
  const toggleEditPayment = (record?: Payment) => {
    setOpenEditPayment((prev) => !prev);
    setPaymentSelected(record || null);
  };
  const { run: updatePaymentEmployee } = useUpdatePaymentEmployee();
  const onUpdatedPayment = async (values: PaymentFormValues) => {
    let params: UpdatePaymentEmployeeParams;
    if (isIreland) {
      params = {
        paymentCode: paymentSelected?.paymentCode || null,
        paymentType: values?.paymentType?.value || null,
        description: values.description ?? '',
        startDate: values.startDate?.format('MM-DD-YYYY') ?? null,
        endDate: values.endDate?.format('MM-DD-YYYY') ?? null,
        minPaymentPeriod: values.minPaymentPeriod
          ? toNumber(values.minPaymentPeriod)
          : null,
        maxRecurrence: values.maxRecurrence
          ? toNumber(values.maxRecurrence)
          : null,
        totalPayment: values.totalPayment || null,
        includeInCisCalculation: values.includeInCisCalculation,
        repetition: values.repetition?.value || null,
        beforeTax: values.beforeTax,
        // NX-5656: the Ireland calc reads taxCanBeDeducted, not beforeTax —
        // keep both in sync so the toggle actually reaches the calculation
        taxCanBeDeducted: values.beforeTax,
        employeePRSICanBeDeducted: values.employeePRSICanBeDeducted,
        employerPRSICanBeDeducted: values.employerPRSICanBeDeducted,
        uscCanBeDeducted: values.uscCanBeDeducted,
        ascCanBeDeducted: values.ascCanBeDeducted,
        pensionSchemaDeductionCanBeDeducted:
          values.pensionSchemaDeductionCanBeDeducted,
      };
    } else if (isCISWorker) {
      params = {
        paymentCode: paymentSelected?.paymentCode || null,
        paymentType: values?.paymentType?.value || null,
        description: values.description ?? '',
        startDate: values.startDate?.format('MM-DD-YYYY') ?? null,
        endDate: values.endDate?.format('MM-DD-YYYY') ?? null,
        minPaymentPeriod: values.minPaymentPeriod
          ? toNumber(values.minPaymentPeriod)
          : null,
        maxRecurrence: values.maxRecurrence
          ? toNumber(values.maxRecurrence)
          : null,
        totalPayment: values.totalPayment || null,
        includeInCisCalculation: values.includeInCisCalculation,
      };
    } else {
      params = {
        paymentCode: paymentSelected?.paymentCode || null,
        paymentType: values?.paymentType?.value || null,
        description: values.description ?? '',
        startDate: values.startDate?.format('MM-DD-YYYY') ?? null,
        endDate: values.endDate?.format('MM-DD-YYYY') ?? null,
        minPaymentPeriod: values.minPaymentPeriod
          ? toNumber(values.minPaymentPeriod)
          : null,
        maxRecurrence: values.maxRecurrence
          ? toNumber(values.maxRecurrence)
          : null,
        totalPayment: values.totalPayment || null,
        beforeTax: values.beforeTax,
        beforeNI: values.beforeNI,
        beforePension: values.beforePension,
      };
    }
    const res = await updatePaymentEmployee(
      paymentSelected!.id,
      employeeId,
      params,
    );
    if (res.success) {
      showToast({
        type: 'success',
        message: messages['common.updateSuccess'],
      });
      refetchData();
    }
    toggleEditPayment();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-[16px]">
        <AppInputSearch onChange={onChangeSearch} />
        {!isEmployee && (
          <AppButton
            type="primary"
            name={messages['common.addNew']}
            leftIcon={<PlusOutlined />}
            onClick={toggleCreatePayment}
          />
        )}
      </div>
      <AppsContent>
        <AppTableContainer
          data={list || []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: isIreland ? 4000 : 2000, y: 700 }}
          titleEmpty={messages['employee.payment.empty']}
          columns={columns}
          onChangeTable={onChangeTable}
          onChangePagination={onChangePagination}
        />
      </AppsContent>
      <CreatePaymentModal
        isOpen={openCreatePayment}
        toggle={toggleCreatePayment}
        onSubmit={onCreatedPayment}
        paymentModel={paymentModel}
      />
      <EditPaymentModal
        isOpen={openEditPayment}
        toggle={toggleEditPayment}
        data={paymentSelected}
        onSubmit={onUpdatedPayment}
        paymentModel={paymentModel}
      />
    </div>
  );
};

export default PaymentComponent;
